import { useEffect, useState } from 'react';
import { MiniTicketCard } from '@/components/mini-ticket-card';
import type { Ticket, Project } from '@mangelapp/shared';
import { parseWhatsAppTicketRef } from '@/utils/whatsapp-ticket-ref';
import { LogoLoader } from '@/components/ui/logo-loader';

/**
 * Get the API base URL from environment, matching queryClient.ts pattern.
 */
function getApiBase(): string {
  return import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:3000' : '');
}

/**
 * Internal page that renders a MiniTicketCard for screenshot capture.
 * 
 * URL: /ticket-card-image?ref=<encodedRef>&token=<signedToken>
 * 
 * This page is used by the API's Puppeteer-based image generation service.
 * It validates the signed token, fetches the ticket via public API, and
 * renders the MiniTicketCard in a minimal viewport suitable for screenshotting.
 * 
 * Security: The token is validated by checking HMAC signature with shared secret.
 * The token format is: HMAC(ref + expiry, secret) where expiry is Unix timestamp.
 * 
 * Once rendered, the page sets data-ready="true" on the card container so
 * Puppeteer can wait for that selector before taking the screenshot.
 */
export default function TicketCardImageRenderer() {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    async function loadTicketData() {
      try {
        // Parse URL params
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('ref');
        const token = params.get('token');

        if (!ref || !token) {
          setError('Missing required parameters');
          setIsLoading(false);
          return;
        }

        // Validate token (POST to API for validation)
        const apiBase = getApiBase();
        const tokenValidation = await fetch(`${apiBase}/api/ticket-card-image/validate-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ref, token }),
        });

        if (!tokenValidation.ok) {
          const result = await tokenValidation.json();
          setError(result.message || 'Invalid or expired token');
          setIsLoading(false);
          return;
        }

        // Parse the ref to get ticketId
        const parsed = parseWhatsAppTicketRef(ref);
        if (!parsed) {
          setError('Invalid ticket reference');
          setIsLoading(false);
          return;
        }

        const { ticketId } = parsed;

        // Get language from URL params (passed by the screenshot service)
        const lang = params.get('lang') || 'en';

        // Fetch ticket via public endpoint (use plain fetch to avoid auth redirects)
        // Pass language via Accept-Language header for translated fields
        const ticketResponse = await fetch(`${apiBase}/api/tickets/${ticketId}/public`, {
          headers: {
            'Accept-Language': lang,
          },
        });
        if (!ticketResponse.ok) {
          setError('Failed to fetch ticket');
          setIsLoading(false);
          return;
        }

        const ticketResult = await ticketResponse.json();
        if (!ticketResult.success || !ticketResult.data) {
          setError('Ticket not found');
          setIsLoading(false);
          return;
        }

        const ticketData = ticketResult.data as Ticket;
        setTicket(ticketData);

        // Try to fetch project (may fail for public view, that's OK)
        if (ticketData.projectId) {
          try {
            const projectResponse = await fetch(`${apiBase}/api/projects/${ticketData.projectId}/public`);
            if (projectResponse.ok) {
              const projectResult = await projectResponse.json();
              if (projectResult.success && projectResult.data) {
                setProject(projectResult.data as Project);
              }
            }
          } catch {
            // Ignore project fetch errors for public view
          }
        }

        setIsLoading(false);
        // Small delay to ensure styles are applied
        setTimeout(() => setIsReady(true), 100);
      } catch (err) {
        console.error('Error loading ticket data:', err);
        setError('Failed to load ticket');
        setIsLoading(false);
      }
    }

    loadTicketData();
  }, []);

  if (isLoading) {
    return (
      <div className="w-[375px] h-[800px] flex items-center justify-center bg-gray-100">
        <LogoLoader size="md" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="w-[375px] h-[200px] flex items-center justify-center bg-red-50 text-red-600 text-sm p-4 text-center">
        {error || 'Ticket not found'}
      </div>
    );
  }

  return (
    <div 
      className="w-[375px] bg-gray-100 p-0"
      data-ready={isReady ? 'true' : 'false'}
      id="ticket-card-container"
    >
      <MiniTicketCard
        ticket={ticket}
        project={project ?? undefined}
        isPublicView
        className="shadow-none"
      />
    </div>
  );
}
